// components/TimeSlots.jsx
import React from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import { Add, DeleteOutline, DragIndicator } from "@mui/icons-material";
import CustomTimePicker from "../../../../components/FormFields/TiimePicker/TimePicker";

const TimeSlots = ({ selectedDays, customTimeData, onTimeChange, onAddSlot, onRemoveSlot, weeklyAssignment }) => {
  const theme = useTheme();

  if (selectedDays.length === 0) return null;

  const calculateDayTotal = (day) => {
    let totalMinutes = 0;
    const slots = customTimeData[day] || [];
    if (Array.isArray(slots)) {
      slots.forEach((slot) => {
        if (slot.time_required_from && slot.time_required_to) {
          const [fH, fM] = slot.time_required_from.split(":").map(Number);
          const [tH, tM] = slot.time_required_to.split(":").map(Number);
          let fromMin = fH * 60 + fM;
          let toMin = tH * 60 + tM;
          if (toMin < fromMin) toMin += 1440;
          totalMinutes += toMin - fromMin;
        }
      });
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack spacing={1.5}>
        {selectedDays.map((day) => {
          const slots = Array.isArray(customTimeData[day]) ? customTimeData[day] : [];
          return (
            <Box
              key={day}
              sx={{
                p: 1.5,
                bgcolor: "#FFFFFF",
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                borderRadius: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                },
              }}
            >
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={theme.palette.primary.main}
                    sx={{ fontSize: "0.7rem", letterSpacing: "0.5px", textTransform: "uppercase" }}
                  >
                    {day}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Add sx={{ fontSize: 14 }} />}
                    onClick={() => onAddSlot(day)}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                      minHeight: 26,
                      px: 1.5,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    Add Slot
                  </Button>
                </Box>

                <Stack spacing={1}>
                  {slots.map((slot, index) => {
                    const existingSlot = weeklyAssignment?.find(job => job.id === slot.id);
                    const timeFrom = slot.time_required_from || existingSlot?.time_required_from || "";
                    const timeTo = slot.time_required_to || existingSlot?.time_required_to || "";
                    
                    return (
                      <Paper
                        key={slot.id || index}
                        elevation={0}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1,
                          bgcolor: alpha(theme.palette.background.default, 0.3),
                          border: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                          borderRadius: "6px",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                            borderColor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <DragIndicator
                          sx={{
                            fontSize: 16,
                            color: alpha(theme.palette.text.secondary, 0.2),
                            cursor: "grab",
                          }}
                        />

                        <Box sx={{ flex: 1, display: "flex", gap: 1.5, alignItems: "center" }}>
                          <Box sx={{ flex: 1, minWidth: 100 }}>
                            <CustomTimePicker
                              size="small"
                              label="From"
                              value={timeFrom}
                              onChange={(e) => {
                                const val = e?.target?.value || e;
                                if (val) {
                                  onTimeChange(day, index, "time_required_from", val);
                                }
                              }}
                              sx={{
                                "& .MuiInputBase-input": {
                                  fontSize: "0.75rem",
                                  py: 1,
                                },
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                          >
                            to
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 100 }}>
                            <CustomTimePicker
                              size="small"
                              label="To"
                              value={timeTo}
                              onChange={(e) => {
                                const val = e?.target?.value || e;
                                if (val) {
                                  onTimeChange(day, index, "time_required_to", val);
                                }
                              }}
                              sx={{
                                "& .MuiInputBase-input": {
                                  fontSize: "0.75rem",
                                  py: 1,
                                },
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {existingSlot && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "3px",
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                color: theme.palette.success.main,
                                fontSize: "0.5rem",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Existing
                            </Box>
                          )}
                          {!existingSlot && slot.id && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "3px",
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                color: theme.palette.warning.main,
                                fontSize: "0.5rem",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              New
                            </Box>
                          )}
                          {!slot.id && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "3px",
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                color: theme.palette.warning.main,
                                fontSize: "0.5rem",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              New
                            </Box>
                          )}

                          <IconButton
                            size="small"
                            onClick={() => onRemoveSlot(day, index)}
                            sx={{
                              color: alpha(theme.palette.error.main, 0.4),
                              p: 0.5,
                              "&:hover": {
                                color: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.04),
                              },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>

                {slots.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.6rem" }}
                    >
                      {slots.length} slots • {calculateDayTotal(day)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default TimeSlots;